/**
 * Tooltip help content for proficiency and shop culture sliders
 * Subcategories are sourced from jobs_catalog sub_menu taxonomy
 */

export interface ProficiencyHelpContent {
  description: string;
  subcategories: string[];
}

export interface ShopCultureHelpContent {
  explanation: string;
  example: string;
}

/**
 * Help content for each proficiency category
 * Subcategories match the jobs_catalog sub_menu values
 */
export const PROFICIENCY_HELP: Record<string, ProficiencyHelpContent> = {
  'ADAS': {
    description: 'Influences how strongly OptiBay prefers this technician for Advanced Driver Assistance System calibration work.',
    subcategories: ['Static Calibrations', 'Dynamic Calibrations', 'Camera Systems', 'Radar Systems', 'Lidar / Ultrasonic', 'Control Modules', 'Sensor Replacement']
  },
  'Alignment': {
    description: 'Influences preference for wheel alignment and related suspension geometry work.',
    subcategories: ['Two-Wheel Alignment', 'Four-Wheel Alignment', 'Thrust Angle', 'Camber / Caster / Toe', 'Alignment Diagnosis']
  },
  'Body & Frame': {
    description: 'Influences preference for structural and body repair work.',
    subcategories: ['Frame Inspection', 'Structural Repair', 'Panel Replacement', 'Rust Repair', 'Underbody Protection']
  },
  'Brakes': {
    description: 'Influences preference for brake system repairs and service.',
    subcategories: ['Brake Service', 'Front Brake System', 'Rear Brake System', 'ABS / Stability', 'Parking Brake', 'Hydraulics', 'Wheel End / Hubs & Bearings']
  },
  'Detailing': {
    description: 'Influences preference for vehicle detailing and appearance work. Detail skill is its own proficiency and never counts toward mechanical skill.',
    subcategories: ['Exterior Wash', 'Exterior Detail', 'Interior Detail', 'Carpet / Upholstery', 'Leather Care', 'Paint Correction / Polish', 'Wax / Sealant / Ceramic', 'Headlight Restoration', 'Engine Bay Detail']
  },
  'Diagnostics': {
    description: 'Influences preference for complex diagnostic and troubleshooting work.',
    subcategories: ['Scan Tool Diagnostics', 'Code Reading', 'Data Analysis', 'System Testing', 'Intermittent Issues', 'Electrical Diagnosis']
  },
  'Drivetrain': {
    description: 'Influences preference for driveline and axle-related repairs.',
    subcategories: ['Driveshaft', 'U-Joints / CV Joints', 'Differential', 'Transfer Case', 'Axle Shafts', 'Wheel Bearings']
  },
  'Electrical': {
    description: 'Influences preference for electrical system repairs and wiring work.',
    subcategories: ['Wiring Repair', 'Lighting Systems', 'Power Windows / Locks', 'Instrument Cluster', 'Body Control Module', 'Accessory Installation']
  },
  'Engine Mechanical': {
    description: 'Influences preference for internal engine repairs and rebuilds.',
    subcategories: ['Timing Belt / Chain', 'Head Gasket', 'Valve Train', 'Engine Rebuild', 'Piston / Rings', 'Crankshaft / Bearings', 'Oil Pump']
  },
  'Engine Performance': {
    description: 'Influences preference for engine performance and drivability work.',
    subcategories: ['Fuel System', 'Ignition System', 'Emission Controls', 'Intake / Exhaust', 'Turbo / Supercharger', 'Engine Management', 'Drivability Diagnosis']
  },
  'EV / Hybrid High Voltage': {
    description: 'Influences preference for electric and hybrid vehicle high-voltage system work.',
    subcategories: ['Battery Pack Service', 'High Voltage Safety', 'Electric Motor', 'Inverter / Converter', 'Charging System', 'Regenerative Braking', 'Thermal Management']
  },
  'Heating Ventilation A/C': {
    description: 'Influences preference for climate control system repairs.',
    subcategories: ['A/C Service', 'A/C Diagnosis', 'Compressor Replacement', 'Evaporator / Condenser', 'Heater Core', 'Blower Motor', 'Climate Control']
  },
  'Inspections': {
    description: 'Influences preference for vehicle inspection and evaluation work.',
    subcategories: ['State Inspection', 'Pre-Purchase Inspection', 'Multi-Point Inspection', 'DVI (Digital Vehicle Inspection)', 'Safety Inspection', 'Emission Inspection']
  },
  'Maintenance': {
    description: 'Influences preference for routine maintenance and service work.',
    subcategories: ['Oil Change', 'Filter Replacement', 'Fluid Services', 'Spark Plugs', 'Belt Replacement', 'Battery Service', 'Wiper Blades', 'Light Bulbs']
  },
  'Restraints': {
    description: 'Influences preference for safety restraint system repairs.',
    subcategories: ['Airbag Diagnosis', 'Airbag Replacement', 'Seatbelt Service', 'Occupant Sensors', 'Crash Sensors', 'SRS Module']
  },
  'Starting & Charging': {
    description: 'Influences preference for starting and charging system repairs.',
    subcategories: ['Starter Replacement', 'Alternator Replacement', 'Battery Replacement', 'Charging System Diagnosis', 'Parasitic Draw', 'Jump Start / Boost']
  },
  'Steering & Suspension': {
    description: 'Influences preference for steering and suspension component repairs.',
    subcategories: ['Struts / Shocks', 'Control Arms', 'Ball Joints', 'Tie Rods', 'Sway Bar Links', 'Power Steering', 'Steering Rack', 'Bushings']
  },
  'Tires': {
    description: 'Influences preference for tire service and wheel work.',
    subcategories: ['Tire Installation', 'Tire Rotation', 'Tire Balance', 'Flat Repair', 'TPMS Service', 'Wheel Mounting', 'Tire Pressure Check']
  },
  'Transmission': {
    description: 'Influences preference for transmission repairs and service.',
    subcategories: ['Transmission Service', 'Clutch Replacement', 'Torque Converter', 'Valve Body', 'Transmission Rebuild', 'Shift Cable / Linkage', 'Transmission Diagnosis']
  },
};

/**
 * Help content for shop culture sliders
 * Each entry includes explanation and real-world example
 */
export const SHOP_CULTURE_HELP: Record<string, ShopCultureHelpContent> = {
  'willingnessToReassign': {
    explanation: 'Controls how freely OptiBay will move tickets between technicians to optimize the schedule.',
    example: 'At 80%, if Tech A is overloaded and Tech B has capacity, OptiBay may move a brake job from A to B even if A was originally assigned.'
  },
  'specialistGapSplitting': {
    explanation: 'The minimum skill difference (in tiers) required before OptiBay suggests splitting a multi-job ticket between technicians.',
    example: 'At tier gap 5, a ticket with both oil change (Tier 1) and transmission work (Tier 7) might be split so each tech handles their specialty.'
  },
  'workloadBalancePreference': {
    explanation: 'Balance between spreading work evenly vs. clustering work on the best-fit technician.',
    example: 'At 0.2 (toward balance), OptiBay distributes tickets evenly. At 0.8 (specialist mode), your best brake tech might get 80% of brake jobs.'
  },
  'comebackNudge': {
    explanation: 'Priority boost for assigning comeback/warranty work back to the original technician.',
    example: 'At 100, if a customer returns for the same issue, OptiBay strongly prefers the tech who did the original work to maintain accountability.'
  },
  'willingnessRollPastEod': {
    explanation: 'Maximum minutes OptiBay will schedule work past the shop\'s closing time.',
    example: 'At 30 minutes, a 4:45 PM job on a 5 PM close day could finish at 5:30 PM. At 0, OptiBay strictly honors closing time.'
  },
  'presentVehiclePriority': {
    explanation: 'Time window for prioritizing vehicles already in the shop over appointments that haven\'t arrived.',
    example: 'At 60 minutes, if a 2:00 PM appointment hasn\'t arrived but a walk-in shows up at 1:45 PM, OptiBay may start the walk-in first.'
  },
  'ticketSplittingEnabled': {
    explanation: 'Master toggle for allowing OptiBay to suggest splitting multi-job tickets between technicians.',
    example: 'When enabled, a ticket with both tire rotation and engine diagnostics could be split to match each job with the right specialist.'
  },
  'minSplitMinutes': {
    explanation: 'Minimum work time required for each part of a split ticket.',
    example: 'At 30 minutes, OptiBay won\'t create a split where one part is only 15 minutes—it ensures meaningful work chunks for each tech.'
  },
  'crossTechFitnessThreshold': {
    explanation: 'How much better a reassignment must be before OptiBay suggests moving a ticket to a different technician.',
    example: 'At 20 points, OptiBay only suggests moving a ticket if the new tech scores 20+ points higher. Higher values = fewer suggestions.'
  },
  'proficiencyGapThreshold': {
    explanation: 'Maximum proficiency drop allowed when reassigning a ticket to balance workload or timing.',
    example: 'At 40%, if Tech A (90% brakes) is busy, OptiBay can reassign to Tech B (50% brakes) since the 40% gap is within tolerance.'
  },
  'waiterTimeFlexMin': {
    explanation: 'How many minutes waiter appointments can flex around their scheduled time.',
    example: 'At 15 minutes, a 10:00 AM oil change waiter could be pushed to 10:15 if a drop-off in progress runs slightly over.'
  },
  'eodRiskThresholdMin': {
    explanation: 'How close to a technician\'s end of day a planned finish must be before OptiBay flags the ticket as at risk of running past close.',
    example: 'At 30 minutes, a job planned to finish at 4:40 PM for a technician who leaves at 5:00 PM is flagged as an end-of-day risk.'
  },
  'noShowGraceThresholdMin': {
    explanation: 'How many minutes after a scheduled technician shift start before OptiBay should treat an unclocked technician as no-show risk. This setting is foundational only right now and does not yet change scheduling behavior.',
    example: 'At 15 minutes, a technician scheduled to start at 8:00 AM who has not clocked in by 8:15 AM will be considered no-show risk once this is wired into scheduling in a future phase.'
  },
};
